/**
 * SKE-4550 — the M4 "Update Instagram publishing" campaign funnel stages.
 *
 * Names only. The call sites belong to the surfaces that fire them, and those
 * surfaces do not exist yet: the banner, the sidebar entry and the campaign
 * page are their own items. This file exists so all six stages are spelled the
 * same in the browser as on the server, rather than being typed out twice and
 * drifting by one hyphen.
 *
 * Must stay identical to `CAMPAIGN_EVENTS` in
 * `apps/schedugram-server/src/app/services/instagram-publishing/campaignTelemetry.ts`.
 *
 * WHICH SIDE FIRES WHAT, and why it is split this way:
 *
 * - `eligible`, `prompted`, `started` and `abandoned` are fired HERE. Only the
 *   browser knows when a surface first rendered, when a button was pressed and
 *   when someone walked away, and only the browser can fire each once per
 *   session. Firing `eligible` from the status endpoint instead would count one
 *   event per poll, from every surface, which is not a funnel.
 * - `completed` and `scopeNotGranted` are fired by the SERVER, because they
 *   carry the re-derived outcome and only the server knows it. Do not fire
 *   either of them from the browser; two sources for one stage is how a funnel
 *   ends up double counting.
 *
 * `scopeNotGranted` is the stage the whole ticket exists for. A customer who
 * unticks publishing on Facebook's consent screen completes the flow and
 * changes nothing, so it is never folded into success and never into
 * abandonment.
 *
 * Carry counts as metadata, never a handle, an account id or an email.
 */
export const INSTAGRAM_CAMPAIGN_EVENTS = {
  /** This workspace has Instagram accounts still to update. Once per session. */
  eligible: "ig-publishing-update-eligible",
  /** A campaign surface was actually shown. */
  prompted: "ig-publishing-update-prompted",
  /** Update now was pressed and the Facebook dialog opened. */
  started: "ig-publishing-update-started",
  /** Server-fired. Listed here so nobody adds a second source for it. */
  completed: "ig-publishing-update-completed",
  /** Server-fired. Listed here for the same reason. */
  scopeNotGranted: "ig-publishing-update-scope-not-granted",
  /** Shown, begun, and nothing came back. */
  abandoned: "ig-publishing-update-abandoned",
} as const;

export type InstagramCampaignEventName =
  (typeof INSTAGRAM_CAMPAIGN_EVENTS)[keyof typeof INSTAGRAM_CAMPAIGN_EVENTS];

/** The stages the browser is allowed to fire. The other two are the server's. */
export const CLIENT_FIRED_INSTAGRAM_CAMPAIGN_EVENTS = [
  INSTAGRAM_CAMPAIGN_EVENTS.eligible,
  INSTAGRAM_CAMPAIGN_EVENTS.prompted,
  INSTAGRAM_CAMPAIGN_EVENTS.started,
  INSTAGRAM_CAMPAIGN_EVENTS.abandoned,
] as const;
